import React, { useEffect, useState } from "react";
import api, { API_BASE } from "../lib/api";
import { Button } from "../components/ui/button";
import { eur, slotLabel, spaceTypeLabel, formatApiError } from "../lib/utils.helpers";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { Download, X, Ticket } from "lucide-react";

export default function Account() {
  const { user } = useAuth();
  const [reservations, setReservations] = useState([]);
  const [passes, setPasses] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [r, p] = await Promise.all([
        api.get("/reservations/my"),
        api.get("/passes/my"),
      ]);
      setReservations(r.data);
      setPasses(p.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const cancel = async (id) => {
    try {
      await api.patch(`/reservations/${id}/cancel`);
      toast.success("Réservation annulée");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadInvoice = (id) => {
    window.open(`${API_BASE}/reservations/${id}/invoice`, "_blank");
  };

  return (
    <div className="container-x py-12" data-testid="account-page">
      <p className="overline text-[#2B8B8E]">Mon compte</p>
      <h1 className="text-4xl sm:text-5xl font-light mt-2">Bonjour, {user?.name?.split(" ")[0]} 👋</h1>
      <p className="text-muted-foreground mb-10">{user?.email}</p>

      {/* Passes */}
      {passes.length > 0 && (
        <section className="mb-12" data-testid="my-passes-section">
          <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2"><Ticket size={20} className="text-[#2B8B8E]" /> Mes Pass</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {passes.map((p) => (
              <div key={p.id} className={`border-2 rounded-2xl p-5 bg-card ${p.status === "active" ? "border-[#2B8B8E]" : "border-border opacity-70"}`} data-testid={`pass-${p.id}`}>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="overline text-[#2B8B8E] text-[10px]">{p.status === "active" ? "Actif" : p.status === "pending" ? "En attente de paiement" : p.status === "consumed" ? "Épuisé" : "Expiré"}</p>
                    <h3 className="text-xl font-semibold">{p.label}</h3>
                  </div>
                  <p className="text-2xl font-light text-[#2B8B8E]">{p.unlimited ? "∞" : `${p.days_remaining}/${p.days_total}`}</p>
                </div>
                <p className="text-xs text-muted-foreground">
                  {p.unlimited ? "Accès illimité" : "Journées restantes"}
                  {p.valid_until && ` · Valide jusqu'au ${new Date(p.valid_until).toLocaleDateString("fr-FR")}`}
                </p>
              </div>
            ))}
          </div>
        </section>
      )}

      <h2 className="text-2xl font-semibold mb-4">Mes réservations</h2>
      {loading ? <p className="text-muted-foreground">Chargement…</p> :
        reservations.length === 0 ? (
          <div className="border border-dashed border-border rounded-2xl p-12 text-center text-muted-foreground">
            Aucune réservation pour le moment.
          </div>
        ) : (
          <div className="space-y-3" data-testid="reservations-list">
            {reservations.map((r) => (
              <div key={r.id} className="border border-border rounded-2xl p-5 bg-card flex flex-col md:flex-row gap-4 md:items-center justify-between" data-testid={`reservation-row-${r.id}`}>
                <div className="flex-1">
                  <p className="overline text-[#0D2C4D] text-[10px]">{spaceTypeLabel(r.space_type)}</p>
                  <p className="font-semibold mt-1">{r.space_name}</p>
                  <p className="text-sm text-muted-foreground">
                    {r.date} · {r.slot_type === "hour" ? `${r.start_hour}h-${r.end_hour}h` : slotLabel(r.slot_type)} · {r.seats} place(s)
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-[#2B8B8E]">{r.payment_method === "pass" ? "Pass" : eur(r.total_price)}</p>
                  <p className="text-xs text-muted-foreground">
                    {r.status === "cancelled" ? "Annulée" : r.payment_status === "paid" ? (r.payment_method === "pass" ? "Payée avec Pass" : "Payée") : r.payment_method === "on_site" ? "À payer sur place" : "Paiement en attente"}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => downloadInvoice(r.id)} data-testid={`invoice-btn-${r.id}`}>
                    <Download size={14} className="mr-1" /> Facture
                  </Button>
                  {r.status !== "cancelled" && (
                    <Button variant="outline" size="sm" onClick={() => cancel(r.id)} data-testid={`cancel-btn-${r.id}`}>
                      <X size={14} className="mr-1" /> Annuler
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}
